import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Calendar } from "@/components/ui/calendar";
import { ErrorState } from "@/components/error-state";
import { EmptyState } from "@/components/empty-state";
import { eodApi, type EodTotalsDto } from "@/lib/eod";

function iso(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

interface Card { label: string; value: string; }

function groups(t: EodTotalsDto): { title: string; cards: Card[] }[] {
  const money = (n: number) => n.toLocaleString();
  return [
    { title: "Invoicing", cards: [
      { label: "Invoices", value: String(t.invoicesTodayCount) },
      { label: "Total (inc GST)", value: money(t.invoicesTodayTotal) },
      { label: "GST", value: money(t.gstTodayTotal) },
    ] },
    { title: "Payments", cards: [
      { label: "Payments", value: String(t.paymentsReceivedCount) },
      { label: "Received", value: money(t.paymentsReceivedTotal) },
    ] },
    { title: "Refunds & Adjustments", cards: [
      { label: "Refunds", value: money(t.refundsTotal) },
      { label: "Adjustments", value: money(t.adjustmentsTotal) },
    ] },
    { title: "Jobs & Quotes", cards: [
      { label: "Active jobs", value: String(t.activeJobsCount) },
      { label: "New jobs", value: String(t.newJobsCount) },
      { label: "Quotes issued", value: String(t.quotesIssuedCount) },
    ] },
    { title: "Accounts (as of last run)", cards: [
      { label: "Outstanding (non-account)", value: money(t.invoicesOutstandingTotal) },
      { label: "Account balances", value: money(t.clientAccountsTotal) },
      { label: "Overdue", value: money(t.clientAccountsOverdueTotal) },
    ] },
  ];
}

export function EodTotalsPage() {
  const [selected, setSelected] = useState<Date>(new Date());
  const monthKey = useMemo(() => `${selected.getFullYear()}-${String(selected.getMonth() + 1).padStart(2, "0")}`, [selected]);
  const dateKey = iso(selected);

  const calendarQuery = useQuery({ queryKey: ["eod-calendar", monthKey], queryFn: () => eodApi.calendar(monthKey) });
  const totalsQuery = useQuery({ queryKey: ["eod-totals", dateKey], queryFn: () => eodApi.totals(dateKey, "day") });

  const t = totalsQuery.data?.selected ?? null;

  return (
    <div className="flex flex-col gap-4 p-4">
      <h1 className="text-2xl font-semibold">EOD Totals</h1>
      <div className="grid gap-6 md:grid-cols-[auto_1fr]">
        <Calendar mode="single" selected={selected} onSelect={(d) => d && setSelected(d)} />

        <div>
          {totalsQuery.isError ? (
            <ErrorState onRetry={() => { void totalsQuery.refetch(); }} />
          ) : !t ? (
            <EmptyState headline="No EOD run recorded for this date yet"
              description="Totals appear once the nightly EOD job has processed this date." />
          ) : (
            <div className="flex flex-col gap-6">
              {groups(t).map((g) => (
                <section key={g.title}>
                  <h2 className="mb-2 text-sm font-medium text-muted-foreground">{g.title}</h2>
                  <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                    {g.cards.map((c) => (
                      <div key={c.label} className="rounded-lg border p-3">
                        <div className="text-xs text-muted-foreground">{c.label}</div>
                        <div className="text-xl font-semibold">{c.value}</div>
                      </div>
                    ))}
                  </div>
                </section>
              ))}
            </div>
          )}
        </div>
      </div>
      {calendarQuery.isError ? <ErrorState onRetry={() => { void calendarQuery.refetch(); }} /> : null}
    </div>
  );
}
